import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/axiosConfig';
import { cache, CacheKeys, CacheTTL, invalidateBookingsCache } from '../utils/cache';
import AppShell from '../components/layout/AppShell';
import SearchBar from '../components/home/SearchBar';
import CategoryFilter, { CATEGORY_ICONS } from '../components/home/CategoryFilter';
import BusinessPromoBar from '../components/home/BusinessPromoBar';
import BusinessCard from '../components/business/BusinessCard';
import BusinessProfile from '../components/business/BusinessProfile';
import ServicePicker from '../components/business/ServicePicker';
import MyBookings from '../components/business/MyBookings';
import PhotoGallery from '../components/business/PhotoGallery';
import OnboardingTooltip from '../components/ui/OnboardingTooltip';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonGrid, SkeletonProfile } from '../components/ui/SkeletonLoader';
import { useFavorites } from '../hooks/useFavorites';
import { canRescheduleBooking, RESCHEDULE_LIMIT_MESSAGE } from '../utils/booking';
import { isBookingLimitError, getBookingLimitMessage } from '../utils/bookingLimits';
import { filterByCategoryIds, parseStoredCategoryIds } from '../utils/categoryFilters';
import { useBookingLimits } from '../hooks/useBookingLimits';
import BookingLimitBanner from '../components/ui/BookingLimitBanner';
import '../styles/LandingPage.css';
import QuickRebookCard from '../components/home/QuickRebookCard';
import { useQuickRebook } from '../hooks/useQuickRebook';
import useDocumentTitle from '../hooks/useDocumentTitle';

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, authChecked } = useAuth();
  const businessIdFromUrl = useMemo(() => {
    const fromRouter = searchParams.get('businessid');
    if (fromRouter) return fromRouter;
    return new URLSearchParams(window.location.search).get('businessid');
  }, [searchParams]);
  const viewMode = searchParams.get('view') || new URLSearchParams(window.location.search).get('view');
  const bookingsRef = useRef(null);
  const hasBrowsedSalonRef = useRef(false);

  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(() =>
    parseStoredCategoryIds(localStorage.getItem('adwary_last_category'))
  );
  const [loading, setLoading] = useState(!businessIdFromUrl);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [promoVisible, setPromoVisible] = useState(false);

  const [account, setAccount] = useState(null);
  // Home shows just the app name; a salon page shows the salon's name.
  useDocumentTitle(businessIdFromUrl ? (account?.title || '') : '');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [selectedReservationType, setSelectedReservationType] = useState(null);

  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [deleteBookingId, setDeleteBookingId] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => {
    return localStorage.getItem('adwary_show_favorites') === 'true';
  });
  const { favorites, isFavorite, toggleFavorite, removeFavorite, pruneFavorites } = useFavorites();
  const {
    can_book: canBookByLimit,
    message: bookingLimitMessage,
    daily_actions_used: dailyActionsUsed,
    daily_limit: dailyLimit,
    refreshBookingLimits,
  } = useBookingLimits(isAuthenticated);

  const {
    visible: quickRebookVisible,
    lastBooking: quickRebookData,
    slots: quickRebookSlots,
    selectedSlot: quickRebookSelectedSlot,
    loading: quickRebookLoading,
    booking: quickRebookBooking,
    selectSlot: handleQuickRebookSelectSlot,
    submitBooking: handleQuickRebookSubmit,
    dismiss: handleQuickRebookDismiss,
  } = useQuickRebook({
    isAuthenticated,
    hasBrowsedSalonThisSession: hasBrowsedSalonRef.current,
  });

  useEffect(() => {
    if (businessIdFromUrl) setLoading(false);
  }, [businessIdFromUrl]);

  useEffect(() => {
    localStorage.setItem('adwary_last_category', JSON.stringify(selectedCategories));
  }, [selectedCategories]);

  useEffect(() => {
    localStorage.setItem('adwary_show_favorites', showFavoritesOnly ? 'true' : 'false');
  }, [showFavoritesOnly]);

  useEffect(() => {
    if (businessIdFromUrl) return;
    const fetchData = async () => {
      try {
        setError('');
        const cachedBusinesses = cache.get(CacheKeys.businesses);
        if (cachedBusinesses) {
          setBusinesses(cachedBusinesses);
        } else {
          const accountsRes = await apiClient.get('/accounts');
          if (accountsRes.data?.success && Array.isArray(accountsRes.data.accounts)) {
            setBusinesses(accountsRes.data.accounts);
            cache.set(CacheKeys.businesses, accountsRes.data.accounts, CacheTTL.salons);
          } else {
            setBusinesses([]);
          }
        }
        try {
          let cats = cache.get(CacheKeys.categories);
          if (!cats) {
            const categoriesRes = await apiClient.get('/categories');
            if (categoriesRes.data?.success && Array.isArray(categoriesRes.data.categories)) {
              cats = categoriesRes.data.categories;
              cache.set(CacheKeys.categories, cats, CacheTTL.categories);
            }
          }
          if (Array.isArray(cats)) {
            setCategories(cats);
            setSelectedCategories((previous) =>
              previous.filter((selectedId) =>
                cats.some((category) => String(category.id) === String(selectedId))
              )
            );
          }
        } catch (catErr) {
          console.warn('Could not load categories:', catErr);
        }
      } catch (err) {
        setError('لا يمكن تحميل قائمة المحلات. يرجى المحاولة لاحقاً.');
        setBusinesses([]);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [businessIdFromUrl]);

  useEffect(() => {
    if (loading || businessIdFromUrl) return;
    pruneFavorites(businesses.map((b) => b.id));
  }, [businesses, loading, businessIdFromUrl, pruneFavorites]);

  useEffect(() => {
    if (!businessIdFromUrl || !authChecked) {
      if (!businessIdFromUrl) {
        setAccount(null);
        setSelectedReservationType(null);
      }
      return;
    }
    // Track that the user has viewed a salon this JS session so the
    // quick-rebook card doesn't re-appear when they navigate back to the list.
    hasBrowsedSalonRef.current = true;
    let cancelled = false;

    const cachedAccount = cache.get(CacheKeys.business(businessIdFromUrl));
    if (cachedAccount) {
      setAccount(cachedAccount);
      setAccountLoading(false);
      if (cachedAccount.can_book === false && cachedAccount.private_reason === 'not_logged_in') {
        sessionStorage.setItem('loginReturnTo', JSON.stringify({
          pathname: '/',
          search: `?businessid=${businessIdFromUrl}`,
        }));
        navigate(`/login?businessid=${encodeURIComponent(businessIdFromUrl)}`);
      }
      return () => { cancelled = true; };
    }

    setAccountLoading(true);
    setAccountError('');
    apiClient
      .get(`/accounts/${businessIdFromUrl}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.account) {
          const acct = res.data.account;
          setAccount(acct);
          cache.set(CacheKeys.business(businessIdFromUrl), acct, CacheTTL.salons);
          if (acct.can_book === false && acct.private_reason === 'not_logged_in') {
            sessionStorage.setItem('loginReturnTo', JSON.stringify({
              pathname: '/',
              search: `?businessid=${businessIdFromUrl}`,
            }));
            navigate(`/login?businessid=${encodeURIComponent(businessIdFromUrl)}`);
          }
        } else {
          setAccountError('المحل غير موجود');
          setAccount(null);
          removeFavorite(businessIdFromUrl);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setAccountError('المحل غير موجود');
          removeFavorite(businessIdFromUrl);
        } else {
          setAccountError('لا يمكن تحميل بيانات المحل. يرجى المحاولة لاحقاً.');
        }
        setAccount(null);
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setAccountLoading(false);
      });
    return () => { cancelled = true; };
  }, [businessIdFromUrl, isAuthenticated, authChecked, navigate, removeFavorite]);

  useEffect(() => {
    if (viewMode !== 'bookings' || !businessIdFromUrl || accountLoading || !account) return;

    if (!isAuthenticated) {
      sessionStorage.setItem('loginReturnTo', JSON.stringify({
        pathname: '/',
        search: `?businessid=${encodeURIComponent(businessIdFromUrl)}&view=bookings`,
      }));
      navigate(`/login?businessid=${encodeURIComponent(businessIdFromUrl)}&view=bookings`);
      return;
    }

    const timer = setTimeout(() => {
      bookingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [viewMode, businessIdFromUrl, accountLoading, account, isAuthenticated, navigate]);

  const fetchMyBookings = useCallback(() => {
    if (!businessIdFromUrl || !isAuthenticated) {
      setMyBookings([]);
      return;
    }
    const cached = cache.get(CacheKeys.myBookings(businessIdFromUrl));
    if (cached) {
      setMyBookings(cached);
      setMyBookingsLoading(false);
      return;
    }
    setMyBookingsLoading(true);
    apiClient
      .get('/bookings/my-bookings')
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.bookings)) {
          setMyBookings(res.data.bookings);
          cache.set(CacheKeys.myBookings(businessIdFromUrl), res.data.bookings, CacheTTL.bookings);
        } else {
          setMyBookings([]);
        }
      })
      .catch(() => setMyBookings([]))
      .finally(() => setMyBookingsLoading(false));
  }, [businessIdFromUrl, isAuthenticated]);

  useEffect(() => {
    if (!businessIdFromUrl || !isAuthenticated) {
      setMyBookings([]);
      return;
    }
    fetchMyBookings();
  }, [businessIdFromUrl, isAuthenticated, fetchMyBookings]);

  const handleDeleteBooking = (bookingId) => {
    if (!bookingId || deleteBookingId) return;
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    setDeleteBookingId(bookingId);
    apiClient
      .delete(`/bookings/${bookingId}`)
      .then((res) => {
        if (res.data?.success) {
          toast.success(res.data.message || 'تم حذف الحجز بنجاح');
          invalidateBookingsCache();
          refreshBookingLimits();
          fetchMyBookings();
        } else {
          toast.error(res.data.message || 'فشل حذف الحجز');
        }
      })
      .catch((err) => {
        if (isBookingLimitError(err)) {
          refreshBookingLimits();
          toast.info(getBookingLimitMessage(err));
          return;
        }
        toast.error(err.response?.data?.message || 'فشل حذف الحجز');
      })
      .finally(() => setDeleteBookingId(null));
  };

  const handleRescheduleBooking = (booking) => {
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    if (!canRescheduleBooking(booking)) {
      toast.error(RESCHEDULE_LIMIT_MESSAGE);
      return;
    }
    sessionStorage.setItem('selectedBookingType', String(booking.reservation_type_id));
    sessionStorage.setItem('selectedBookingTypeName', booking.booking_type || '');
    navigate(
      `/calendar?businessid=${encodeURIComponent(businessIdFromUrl)}&reschedule=${booking.id}`
    );
  };

  const handleBusinessClick = (businessId) => {
    navigate(`/?businessid=${encodeURIComponent(businessId)}`);
  };

  const handleReservationTypeSelect = (rt) => {
    setSelectedReservationType((prev) => (prev?.id === rt.id ? null : rt));
  };

  const handleBookClick = () => {
    if (!selectedReservationType || !businessIdFromUrl) return;
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    sessionStorage.setItem('selectedBookingType', String(selectedReservationType.id));
    sessionStorage.setItem('selectedBookingTypeName', selectedReservationType.name || '');
    navigate(`/calendar?businessid=${encodeURIComponent(businessIdFromUrl)}`);
  };

  const handleBookNowFromEmpty = () => {
    document.querySelector('.service-picker')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBackToList = () => {
    navigate('/');
    setAccount(null);
    setSelectedReservationType(null);
  };

  const handleShareSalon = async () => {
    const url = `${window.location.origin}/?businessid=${encodeURIComponent(businessIdFromUrl)}`;
    const shareData = {
      title: account?.title || 'ادواري',
      text: `احجز موعدك في ${account?.title || 'الصالون'} عبر ادواري`,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('تم نسخ الرابط');
    }
  };

  if (businessIdFromUrl) {
    const listNavBack = {
      onClick: handleBackToList,
      label: 'الصالونات',
      ariaLabel: 'العودة لقائمة المحلات',
    };

    if (accountLoading || !authChecked || (!account && !accountError)) {
      return (
        <AppShell navBack={listNavBack}>
          <div className="page-container">
            <SkeletonProfile />
          </div>
        </AppShell>
      );
    }

    if (accountError || !account) {
      return (
        <AppShell navBack={listNavBack}>
          <div className="page-container">
            <EmptyState
              icon="⚠️"
              title="تعذّر تحميل المحل"
              message={accountError || 'المحل غير موجود'}
              actionLabel="العودة للصالونات"
              onAction={handleBackToList}
            />
          </div>
        </AppShell>
      );
    }

    const reservationTypes = account.reservation_types || [];
    const canBook = account.can_book !== false && canBookByLimit;

    return (
      <AppShell navBack={listNavBack}>
        <div className="page-container">
          {!canBookByLimit && (
            <BookingLimitBanner
              message={bookingLimitMessage}
              dailyActionsUsed={dailyActionsUsed}
              dailyLimit={dailyLimit}
            />
          )}
          <div className="business-detail-layout">
            <aside className="business-detail-sidebar">
              <BusinessProfile account={account} onShare={handleShareSalon} />

              <div className="business-message-banner">
                <h3>رسالة من المحل</h3>
                <p>
                  {account.description ||
                    'لنوفر لك أفضل خدمة بدون انتظار، احجز موعدك الآن والتزم بالوقت المحدد — نحن بانتظارك!'}
                </p>
              </div>
            </aside>

            <div className="business-detail-main">
              {!canBook ? (
                <div className="business-private-notice">
                  <svg className="business-private-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <h3 className="business-private-title">هذا الصالون خاص</h3>
                  <p className="business-private-msg">
                    {account.private_reason === 'blocked'
                      ? 'تم حظرك من الحجز في هذا الصالون.'
                      : 'عذراً، هذا الصالون يقبل حجوزات لمجموعة مغلقة فقط ولا يمكنك الحجز فيه.'}
                  </p>
                </div>
              ) : (
                <>
                  <ServicePicker
                    reservationTypes={reservationTypes}
                    selectedType={selectedReservationType}
                    onSelect={handleReservationTypeSelect}
                  />
                  <div className="sticky-book-cta">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleBookClick}
                      disabled={!selectedReservationType || !canBookByLimit}
                    >
                      احجز موعدك
                    </button>
                  </div>
                </>
              )}

              <div ref={bookingsRef}>
                <MyBookings
                  isAuthenticated={isAuthenticated}
                  businessId={businessIdFromUrl}
                  bookings={myBookings}
                  loading={myBookingsLoading}
                  deleteBookingId={deleteBookingId}
                  onDelete={handleDeleteBooking}
                  onReschedule={handleRescheduleBooking}
                  onBookNow={handleBookNowFromEmpty}
                  defaultExpanded={viewMode === 'bookings'}
                  bookingActionsDisabled={!canBookByLimit}
                  salonTitle={account?.title}
                  salonAddress={account?.address}
                />
              </div>
            </div>
          </div>

          <PhotoGallery accountId={account.id} photos={account.photos} />
        </div>
      </AppShell>
    );
  }

  const filteredBusinesses = !searchQuery.trim()
    ? businesses
    : businesses.filter(
        (b) =>
          (b.title && b.title.toLowerCase().includes(searchQuery.trim().toLowerCase())) ||
          (b.address && b.address.toLowerCase().includes(searchQuery.trim().toLowerCase()))
      );

  const categoryFilteredBusinesses = filterByCategoryIds(
    filteredBusinesses,
    selectedCategories
  );

  const favoriteBusinesses = filteredBusinesses.filter((b) => favorites.includes(b.id));

  const favoriteGroups = useMemo(() => {
    if (!showFavoritesOnly || favoriteBusinesses.length === 0) return [];

    const groups = [];
    const groupedIds = new Set();

    categories.forEach((cat) => {
      const items = favoriteBusinesses.filter((b) => b.category_id === cat.id);
      if (items.length > 0) {
        groups.push({ category: cat, businesses: items });
        items.forEach((b) => groupedIds.add(b.id));
      }
    });

    const uncategorized = favoriteBusinesses.filter((b) => !groupedIds.has(b.id));
    if (uncategorized.length > 0) {
      groups.push({ category: null, businesses: uncategorized });
    }

    return groups;
  }, [showFavoritesOnly, favoriteBusinesses, categories]);

  const displayedBusinesses = showFavoritesOnly
    ? favoriteBusinesses
    : categoryFilteredBusinesses;

  const handleCategoryChange = (nextCategoryIds) => {
    setSelectedCategories(nextCategoryIds);
    setShowFavoritesOnly(false);
  };

  const searchBar = (
    <SearchBar
      value={searchQuery}
      onChange={setSearchQuery}
      placeholder="ابحث عن محل..."
    />
  );

  return (
    <AppShell headerExtra={searchBar}>
      <div className={`page-container${promoVisible ? ' with-biz-promo' : ''}`}>
        {(quickRebookVisible || quickRebookLoading) && quickRebookData && (
          <QuickRebookCard
            lastBooking={quickRebookData}
            slots={quickRebookSlots}
            selectedSlot={quickRebookSelectedSlot}
            loading={quickRebookLoading}
            booking={quickRebookBooking}
            onSelectSlot={handleQuickRebookSelectSlot}
            onBook={handleQuickRebookSubmit}
            onDismiss={handleQuickRebookDismiss}
            onMoreTimes={() =>
              navigate(`/?businessid=${encodeURIComponent(quickRebookData.businessId)}`)
            }
          />
        )}
        <section className="home-hero">
          <h1 className="home-hero-title">احجز موعدك بسهولة</h1>
          <Link to="/welcome" className="home-hero-link">تعرّف على أدواري ←</Link>
        </section>

        <div className="home-toolbar">
          <div className="home-filter-row">
            <CategoryFilter
              categories={categories}
              selectedCategories={showFavoritesOnly ? [] : selectedCategories}
              onChange={handleCategoryChange}
            />
            {favorites.length > 0 && (
              <button
                type="button"
                className={`home-favorites-filter ${showFavoritesOnly ? 'active' : ''}`}
                onClick={() => setShowFavoritesOnly((v) => !v)}
              >
                ⭐ المفضلة ({favorites.length})
              </button>
            )}
          </div>
          <div className="home-toolbar-mobile-search">{searchBar}</div>
        </div>

        <OnboardingTooltip />

        {loading && <SkeletonGrid count={4} />}
        {error && (
          <EmptyState
            icon="📡"
            title="تعذّر التحميل"
            message={error}
            actionLabel="إعادة المحاولة"
            onAction={() => window.location.reload()}
          />
        )}

        {!loading && !error && businesses.length === 0 && !isAuthenticated && (
          <EmptyState
            icon="🔒"
            title="سجّل الدخول لعرض المحلات"
            message="لا توجد محلات متاحة للعرض حالياً. قد تكون بعض المحلات التي لديك صلاحية الوصول إليها خاصة. سجّل الدخول أو أنشئ حساباً لعرضها."
            actionLabel="تسجيل الدخول / إنشاء حساب"
            onAction={() => navigate('/login')}
          />
        )}

        {!loading && !error && businesses.length === 0 && isAuthenticated && (
          <EmptyState
            icon="🚀"
            title="مرحباً بك في ادواري"
            message="التطبيق حالياً في نسخة تجريبية ومتاح لقائمة محدودة من المحلات والعملاء. ترقبونا... قريباً سنكون متاحين للجميع!"
          />
        )}

        {!loading && !error && businesses.length > 0 && displayedBusinesses.length === 0 && (
          <EmptyState
            icon="🔍"
            title="لا توجد نتائج"
            message={
              showFavoritesOnly
                ? favorites.length === 0
                  ? 'لم تضف أي محل للمفضلة بعد'
                  : 'لا توجد محلات مطابقة في المفضلة'
                : 'جرّب تغيير البحث أو الفئة'
            }
            actionLabel={showFavoritesOnly ? 'عرض الكل' : 'مسح البحث'}
            onAction={() => {
              if (showFavoritesOnly) setShowFavoritesOnly(false);
              else setSearchQuery('');
            }}
          />
        )}

        {!loading && !error && displayedBusinesses.length > 0 && showFavoritesOnly && (
          <div className="favorites-grouped">
            {favoriteGroups.map(({ category, businesses: groupBusinesses }) => (
              <section key={category?.id ?? 'other'} className="favorites-category-section">
                <h2 className="favorites-category-heading">
                  {category?.icon && CATEGORY_ICONS[category.icon] && (
                    <span className="favorites-category-icon">{CATEGORY_ICONS[category.icon]}</span>
                  )}
                  <span>{category?.name_ar || 'أخرى'}</span>
                </h2>
                <div className="business-grid" role="list">
                  {groupBusinesses.map((business) => (
                    <BusinessCard
                      key={business.id}
                      business={business}
                      onClick={handleBusinessClick}
                      isFavorite={isFavorite(business.id)}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {!loading && !error && displayedBusinesses.length > 0 && !showFavoritesOnly && (
          <div className="business-grid" role="list">
            {displayedBusinesses.map((business) => (
              <BusinessCard
                key={business.id}
                business={business}
                onClick={handleBusinessClick}
                isFavorite={isFavorite(business.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}

        <BusinessPromoBar onVisibilityChange={setPromoVisible} />
      </div>
    </AppShell>
  );
}

export default LandingPage;
